/* @jsxImportSource @emotion/react */

import React from 'react'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'

import { Section } from '../Section'
import { WebsiteText } from '../WebsiteText'
import { SingleFAQ } from './SingleFAQ'
import type { FAQSection } from './types'

function nodeToPlainText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return ''
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(nodeToPlainText).join('')
  }
  if (React.isValidElement(node)) {
    const { children } = node.props as { children?: React.ReactNode }
    return nodeToPlainText(children)
  }
  return ''
}

function FAQPageSchema({ faq }: { faq: FAQSection[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((section) => ({
      '@type': 'Question',
      name: nodeToPlainText(section.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: nodeToPlainText(section.answer),
      },
    })),
  }

  return (
    <script
      type="application/ld+json"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Static JSON-LD structured data, no user input
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  )
}

export const SectionFAQ = ({
  className,
  faq,
  background = 'moon-dust-lighter',
  containerVerticalPadding = 80,
}: {
  className?: string
  faq: FAQSection[]
  background?: string
  containerVerticalPadding?: number
}) => {
  return (
    <Section
      background={background}
      containerVerticalPadding={containerVerticalPadding}
    >
      <FAQPageSchema faq={faq} />
      <div
        className={classNames(
          'flexbox flexbox-centered flexbox-column shade-05 margin-auto',
          className
        )}
        css={{
          maxWidth: 780,
        }}
      >
        <div className="text-align-center margin-bottom-32 padding-bottom-24 width-full ">
          <WebsiteText variant="title" htmlTag="h3">
            <FormattedMessage
              id="faq.title"
              defaultMessage="Frequently Asked Questions"
            />
          </WebsiteText>
        </div>
        <div className="flexbox flexbox-column">
          {faq.map((section, index) => (
            <div key={index}>
              {index !== 0 && (
                <div
                  className="width-full margin-bottom-24 margin-top-16"
                  style={{ height: 1, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
                />
              )}
              <SingleFAQ section={section} defaultIsOpen={index === 0} />
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}
